/**
 * commentUtils.js
 *
 * Shared utility functions for handling comments across the application
 */

import { SERVER_URL } from '../config';

/**
 * Add a comment to a post
 * @param {string} postId - The ID of the post to comment on
 * @param {string} commentText - The comment text
 * @param {string} userName - The user's name
 * @param {string} userEmail - The user's email
 * @param {Object} comments - Current comments state
 * @param {Function} setComments - Function to update comments state
 * @param {Object} newComment - Current newComment state
 * @param {Function} setNewComment - Function to update newComment state
 * @param {Function} updatePostCommentCount - Optional callback to update post comment count in parent state
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
export async function addComment(
  postId,
  commentText,
  userName,
  userEmail,
  comments,
  setComments,
  newComment,
  setNewComment,
  updatePostCommentCount = null,
  parentId = null
) {
  if (!commentText || !commentText.trim()) return false;

  try {
    const body = {
      user_name: userName,
      user_email: userEmail,
      content: commentText,
    };
    if (parentId) body.parent_id = parentId;

    const response = await fetch(`${SERVER_URL}/posts/${postId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (response.ok) {
      // Clear input
      setNewComment({ ...newComment, [postId]: '' });

      // Reload comments
      const commentsResponse = await fetch(`${SERVER_URL}/posts/${postId}/comments`);
      if (commentsResponse.ok) {
        const data = await commentsResponse.json();
        setComments({ ...comments, [postId]: data.comments });
      }

      // Update comment count if callback provided
      if (updatePostCommentCount) {
        updatePostCommentCount(postId);
      }

      return true;
    }
    return false;
  } catch (err) {
    console.error('Error adding comment:', err);
    return false;
  }
}

/**
 * Load comments for a post
 * @param {string} postId - The ID of the post
 * @param {Object} comments - Current comments state
 * @param {Function} setComments - Function to update comments state
 * @returns {Promise<void>}
 */
export async function deleteComment(commentId, userEmail, postId, comments, setComments, updatePostCommentCount = null) {
  try {
    const response = await fetch(
      `${SERVER_URL}/comments/${commentId}?user_email=${encodeURIComponent(userEmail)}`,
      { method: 'DELETE' }
    );
    if (response.ok) {
      const commentsResponse = await fetch(`${SERVER_URL}/posts/${postId}/comments`);
      if (commentsResponse.ok) {
        const data = await commentsResponse.json();
        setComments({ ...comments, [postId]: data.comments });
      }
      if (updatePostCommentCount) updatePostCommentCount(postId);
      return true;
    }
    return false;
  } catch (err) {
    console.error('Error deleting comment:', err);
    return false;
  }
}

export async function editComment(commentId, userEmail, content, postId, comments, setComments) {
  try {
    const response = await fetch(`${SERVER_URL}/comments/${commentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_email: userEmail, content }),
    });
    if (response.ok) {
      const commentsResponse = await fetch(`${SERVER_URL}/posts/${postId}/comments`);
      if (commentsResponse.ok) {
        const data = await commentsResponse.json();
        setComments({ ...comments, [postId]: data.comments });
      }
      return true;
    }
    return false;
  } catch (err) {
    console.error('Error editing comment:', err);
    return false;
  }
}

export async function loadComments(postId, comments, setComments) {
  try {
    const response = await fetch(`${SERVER_URL}/posts/${postId}/comments`);
    if (response.ok) {
      const data = await response.json();
      setComments({ ...comments, [postId]: data.comments });
    }
  } catch (err) {
    console.error('Error loading comments:', err);
  }
}
